import axios, { AxiosError } from "axios";
import { toast } from "react-toastify";

import * as Sentry from "@sentry/react";
import {
  OnQueryErrorAction,
  OnQuerySuccessAction,
  QueryMeta,
} from "@src/types/react-query";
import { Logout } from "@src/utils";
import { QueryCache, QueryClient } from "@tanstack/react-query";

const MS_IN_HOUR = 60 * 1000 * 60;
const cacheTime = MS_IN_HOUR * 24;

/**
 * Axios "Network Error" (code `ERR_NETWORK`) is raised by the XHR adapter's
 * `onerror` handler when no server response is ever received — i.e. the client
 * is offline, the request was aborted by a navigation/tab close, or DNS/CORS
 * failed. These are client-side connectivity failures, not actionable
 * application bugs, and reporting them floods Sentry (the same signature
 * appears across every GET endpoint). We skip Sentry capture for them, mirroring
 * the existing `ignoreErrors` filter for the sibling "Request aborted" error.
 */
function isClientConnectivityError(error: AxiosError): boolean {
  return !error.response && error.code === AxiosError.ERR_NETWORK;
}

const recentErrorToasts = new Set<string>();

function shouldShowErrorToast(message: string): boolean {
  if (recentErrorToasts.has(message)) {
    return false;
  }

  recentErrorToasts.add(message);
  setTimeout(() => recentErrorToasts.delete(message), 3000);

  return true;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      onError: (error, query, context) => {
        if (axios.isAxiosError(error)) {
          const is401 = error.response?.status === 401;
          showMutationErrorToast(error);
          if (!is401 && !isClientConnectivityError(error)) {
            Sentry.captureException(error);
          }
        }
      },
    },
    queries: {
      retry: 0,
      staleTime: cacheTime,
    },
  },
  queryCache: new QueryCache({
    onSuccess: (data, query) => {
      if (!query.meta) {
        return;
      }

      const queryMeta = query.meta as QueryMeta;
      if (!queryMeta.onSuccess) {
        return;
      }

      const action = queryMeta.onSuccess as OnQuerySuccessAction | undefined;
      if (!action) {
        return;
      }

      if (typeof action === "string") {
        toast.success(action);
        return;
      }

      if (typeof action === "function") {
        action(data);
        return;
      }
    },
    onError: (error, query) => {
      if (axios.isAxiosError(error)) {
        if (isClientConnectivityError(error)) {
          // Client connectivity failure (offline / navigation abort) with no
          // server response — not an actionable app error, so skip Sentry.
          return;
        }

        if (error.response?.data?.error === "token error") {
          Logout();
          return;
        }

        if (window.location.pathname === "/launch") {
          Logout();
          window.location.reload();
          return;
        }

        const queryMeta = query.meta as QueryMeta | undefined;
        const is401 = error.response?.status === 401;

        if (!queryMeta || !queryMeta.onError || !error.response?.status) {
          // No toast for queries - just Sentry
          if (!is401) {
            Sentry.captureException(error);
          }
          return;
        }

        const action = (queryMeta.onError[error.response.status] ||
          queryMeta.onError["any"] ||
          undefined) as OnQueryErrorAction | undefined;

        if (action === undefined) {
          // No toast for queries - just Sentry
          if (!is401) {
            Sentry.captureException(error);
          }
          return;
        }

        if (action === "ignore" || action === "") {
          return;
        }

        if (typeof action === "function") {
          action(error);
          return;
        }

        if (shouldShowErrorToast(action)) {
          toast.error(action);
        }
      } else {
        // No response from server at all
        Sentry.captureException(error);
      }
    },
  }),
});

export function getMutationErrorMessage(error: any): string | null {
  const status = error.response?.status;
  const data = error.response?.data;
  const serverMessage =
    data?.error?.message ||
    data?.error ||
    data?.message ||
    data?.data?.message ||
    data?.data?.error;

  // Status-specific messages
  switch (status) {
    case 500:
      return "Our servers are having difficulty processing that right now. Please refresh and try again.";
    case 503:
      return "This request is still processing in the background. Check back in a moment.";
    case 403:
      return "You don't have permission to perform this action.";
    case 404:
      return "The resource tied to this couldn't be found, refresh your page and try again!";
  }

  // Use server-provided message for 4xx errors
  if (serverMessage) {
    return serverMessage;
  }

  // Try to extract from Zod tree error format (400 validation errors)
  if (status === 400) {
    const zodMessage = extractZodTreeErrorMessage(data);
    if (zodMessage) {
      return zodMessage;
    }
  }

  if (!error.response) {
    return "Unable to connect. Please check your connection.";
  }

  return null;
}

function extractZodTreeErrorMessage(data: unknown): string | null {
  if (!data || typeof data !== "object") {
    return null;
  }

  const tree = data as Record<string, unknown>;
  if (!("errors" in tree || "properties" in tree || "items" in tree)) {
    return null;
  }

  const messages: string[] = [];
  collectErrorsFromTree(tree, messages);

  return messages[0] ?? null;
}

function collectErrorsFromTree(node: unknown, messages: string[]): void {
  if (!node || typeof node !== "object") {
    return;
  }

  const obj = node as Record<string, unknown>;

  if (Array.isArray(obj.errors)) {
    for (const err of obj.errors) {
      if (typeof err === "string" && err.length > 0) {
        messages.push(err);
      }
    }
  }

  if (obj.properties && typeof obj.properties === "object") {
    for (const value of Object.values(obj.properties)) {
      collectErrorsFromTree(value, messages);
    }
  }

  if (obj.items && typeof obj.items === "object") {
    for (const value of Object.values(obj.items)) {
      collectErrorsFromTree(value, messages);
    }
  }
}

function showMutationErrorToast(error: any) {
  if (error["suppressToast"]) {
    return;
  }

  const serverMessage = error.response?.data?.error;
  if (
    typeof serverMessage === "string" &&
    serverMessage?.includes("Intelligems isn't installed")
  ) {
    return;
  }

  const message = getMutationErrorMessage(error);
  if (!message) {
    return;
  }

  // adminDetail is only present in response if user is admin (backend check)
  const adminDetail = error.response?.data?.adminDetail;
  const fullMessage = adminDetail ? `${message}\n\n${adminDetail}` : message;

  if (shouldShowErrorToast(message)) {
    toast.error(fullMessage);
  }
}
